/**
 * Decision engine / premium dashboard — "It decides what matters."
 * Line-art, currentColor, coherent with DSOS illustration style.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

const viewBox = "0 0 800 450";
const strokeWidth = 1.5;

export function DecisionEngineIllustration({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={cn("w-full max-w-full h-auto text-foreground", className)}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {/* Main frame */}
      <rect
        x="40"
        y="40"
        width="720"
        height="370"
        rx="24"
        className="fill-transparent stroke-current opacity-20 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />

      {/* Left panel: Priority list */}
      <rect
        x="70"
        y="80"
        width="440"
        height="290"
        rx="18"
        className="fill-transparent stroke-current opacity-25 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text
        x="92"
        y="112"
        fontSize="16"
        fontWeight="600"
        className="fill-current opacity-90"
      >
        Priority
      </text>
      <line
        x1="92"
        y1="128"
        x2="490"
        y2="128"
        className="stroke-current opacity-15 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="92" y="148" fontSize="12" fontWeight="600" className="fill-current opacity-50">
        Lead
      </text>
      <text x="460" y="148" fontSize="12" fontWeight="600" textAnchor="end" className="fill-current opacity-50">
        Score
      </text>

      {/* Row 1 — highlighted (top priority) */}
      <rect
        x="90"
        y="165"
        width="400"
        height="56"
        rx="14"
        className="fill-current opacity-[0.06] stroke-current opacity-60 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="112" y="199" fontSize="14" fontWeight="600" className="fill-current opacity-95">
        Lead A
      </text>
      <text x="460" y="200" fontSize="14" fontWeight="700" textAnchor="end" className="fill-current opacity-95">
        92
      </text>
      <rect
        x="112"
        y="174"
        width="122"
        height="22"
        rx="11"
        className="fill-current opacity-10 stroke-current opacity-70 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="173" y="189" fontSize="11" fontWeight="700" textAnchor="middle" className="fill-current opacity-90">
        Top priority
      </text>

      {/* Row 2 */}
      <rect
        x="90"
        y="232"
        width="400"
        height="56"
        rx="14"
        className="fill-transparent stroke-current opacity-20 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="112" y="266" fontSize="14" fontWeight="600" className="fill-current opacity-80">
        Lead B
      </text>
      <text x="460" y="267" fontSize="14" fontWeight="700" textAnchor="end" className="fill-current opacity-50">
        71
      </text>

      {/* Row 3 */}
      <rect
        x="90"
        y="299"
        width="400"
        height="56"
        rx="14"
        className="fill-transparent stroke-current opacity-20 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="112" y="333" fontSize="14" fontWeight="600" className="fill-current opacity-80">
        Lead C
      </text>
      <text x="460" y="334" fontSize="14" fontWeight="700" textAnchor="end" className="fill-current opacity-50">
        58
      </text>

      {/* Right panel: Signals / decision factors */}
      <rect
        x="535"
        y="120"
        width="205"
        height="220"
        rx="18"
        className="fill-transparent stroke-current opacity-25 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="557" y="152" fontSize="14" fontWeight="700" className="fill-current opacity-90">
        Signals
      </text>

      <rect
        x="557"
        y="174"
        width="86"
        height="28"
        rx="14"
        className="fill-transparent stroke-current opacity-30 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="600" y="193" fontSize="12" fontWeight="700" textAnchor="middle" className="fill-current opacity-60">
        Revenue
      </text>

      <rect
        x="557"
        y="214"
        width="86"
        height="28"
        rx="14"
        className="fill-transparent stroke-current opacity-30 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="600" y="233" fontSize="12" fontWeight="700" textAnchor="middle" className="fill-current opacity-60">
        Time
      </text>

      <rect
        x="557"
        y="254"
        width="86"
        height="28"
        rx="14"
        className="fill-current opacity-[0.08] stroke-current opacity-60 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="600" y="273" fontSize="12" fontWeight="800" textAnchor="middle" className="fill-current opacity-90">
        Risk
      </text>

      <rect
        x="657"
        y="174"
        width="63"
        height="108"
        rx="18"
        className="fill-transparent stroke-current opacity-25 stroke-[1.5]"
        vectorEffect="non-scaling-stroke"
      />
      <text x="688.5" y="205" fontSize="11" fontWeight="700" textAnchor="middle" className="fill-current opacity-50">
        Score
      </text>
      <text x="688.5" y="252" fontSize="22" fontWeight="800" textAnchor="middle" className="fill-current opacity-80">
        ↑
      </text>
    </svg>
  );
}
